/**
 * PusherToast — Pusher変更通知のトースト表示
 * 画面右上（AppBarの下）に最大3件表示、3秒で自動消去
 * 新しいトーストが来ると古いものを下に押し出す
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Paper, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { SOURCE_CONFIG, ENTITY_TYPE_LABELS } from '../constants/fieldLabels';

const MAX_TOASTS = 3;
const TOAST_DURATION = 3000;

/** 変更タイプに応じたアイコン */
function getChangeIcon(changeType: string): React.ReactElement {
  switch (changeType) {
    case 'create':
      return <AddCircleOutlineIcon fontSize="small" sx={{ color: '#16a34a' }} />;
    case 'delete':
      return <RemoveCircleOutlineIcon fontSize="small" sx={{ color: '#dc2626' }} />;
    default:
      return <EditIcon fontSize="small" sx={{ color: '#2563eb' }} />;
  }
}

/** 変更タイプに応じたアクセントカラー */
function getAccentColor(changeType: string): string {
  switch (changeType) {
    case 'create': return '#16a34a';
    case 'delete': return '#dc2626';
    default: return '#2563eb';
  }
}

export default function PusherToast(): React.ReactElement | null {
  const [toasts, setToasts] = useState<any[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const addToast = useCallback((record: any) => {
    const toastId = record.id || `toast_${Date.now()}`;

    setToasts(prev => {
      const next = [{ ...record, toastId }, ...prev];
      // 最大件数を超えた古いトーストを削除
      if (next.length > MAX_TOASTS) {
        const removed = next.slice(MAX_TOASTS);
        for (const r of removed) {
          const timer = timersRef.current.get(r.toastId);
          if (timer) {
            clearTimeout(timer);
            timersRef.current.delete(r.toastId);
          }
        }
        return next.slice(0, MAX_TOASTS);
      }
      return next;
    });

    // 自動消去タイマー
    const timer = setTimeout(() => {
      removeToast(toastId);
    }, TOAST_DURATION);
    timersRef.current.set(toastId, timer);
  }, [removeToast]);

  useEffect(() => {
    const handlePusherChange = (e: any) => {
      const record = e.detail;
      if (record) {
        addToast(record);
      }
    };

    window.addEventListener('pusher-change-recorded', handlePusherChange);
    return () => {
      window.removeEventListener('pusher-change-recorded', handlePusherChange);
      // cleanup all timers
      for (const timer of timersRef.current.values()) {
        clearTimeout(timer);
      }
      timersRef.current.clear();
    };
  }, [addToast]);

  if (toasts.length === 0) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 'calc(var(--app-header-height, 64px) + 8px)',
        right: 16,
        zIndex: 9000,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        maxWidth: 360,
        pointerEvents: 'none',
      }}
    >
      {toasts.map((toast: any) => (
        <Paper
          key={toast.toastId}
          elevation={6}
          sx={{
            pointerEvents: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1,
            borderLeft: `4px solid ${getAccentColor(toast.changeType)}`,
            animation: 'pusherToastSlideIn 0.25s ease-out',
            '@keyframes pusherToastSlideIn': {
              from: { opacity: 0, transform: 'translateX(40px)' },
              to: { opacity: 1, transform: 'translateX(0)' },
            },
            minWidth: 280,
          }}
        >
          {getChangeIcon(toast.changeType)}

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 500, fontSize: '0.82rem' }}>
              {toast.summary}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              <Typography variant="caption" sx={{ color: '#8b5cf6', fontWeight: 500, fontSize: '0.68rem' }}>
                {(SOURCE_CONFIG as any)[toast.source]?.icon} {(SOURCE_CONFIG as any)[toast.source]?.label || toast.source}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                · {(ENTITY_TYPE_LABELS as any)[toast.entityType] || toast.entityType}
              </Typography>
            </Box>
          </Box>

          <IconButton
            size="small"
            onClick={() => removeToast(toast.toastId)}
            sx={{ ml: 0.5, p: 0.25 }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Paper>
      ))}
    </Box>
  );
}
